import { put, takeEvery } from 'redux-saga/effects';
import isEmpty from 'lodash/isEmpty';
import { call, select } from 'typed-redux-saga';

import { notify } from '@xcritical/notification';

import {
  crmGridInit,
  crmGridSetItems,
  crmGridSetPage,
  crmGridSetTotalCount,
  gridSelector,
} from '@crm-framework/grid';
import { GRT } from '@crm-framework/types';
import { handleServerError } from '@crm-framework/utils';

import {
  NOTIFICATIONS_DELETE,
  NOTIFICATIONS_MARK_AS_READ,
  NOTIFICATIONS_NEW_RECEIVED,
  NOTIFICATIONS_REFRESH,
  NOTIFICATIONS_START_INIT,
  notificationsActions,
} from './NotificationsStore';
import {
  NotificationsDeleteAction,
  NotificationsMarkAsReadAction,
  NotificationsNewReceivedAction,
} from './types';
import { getNotificationData } from './selectors';
import { notificationsApi } from './api';
import {
  deleteNotificationGridItems,
  getNotificationMessage,
  mapNotificationsToGridItems,
  mapNotificationToGridItem,
  updateNotificationGridItemsIsRead,
} from './adapters';
import { NOTIFICATION_TYPE_ALL, NOTIFICATIONS_GRID_NAME } from './consts';
import { getBusinessUnits, getBUFilterValue } from './businessUnitFilterSaga';
import { INotificationGridItem } from './components/notification-grid';

export function* watchNotifications() {
  yield takeEvery(NOTIFICATIONS_START_INIT, handleStartInit);
  yield takeEvery(NOTIFICATIONS_REFRESH, handleRefresh);
  yield takeEvery(NOTIFICATIONS_MARK_AS_READ, handleMarkAsRead);
  yield takeEvery(NOTIFICATIONS_DELETE, handleDelete);
  yield takeEvery(NOTIFICATIONS_NEW_RECEIVED, handleNewReceived);
}

function* handleStartInit() {
  yield call(getBusinessUnits);
  try {
    const { notificationTypes }: GRT<typeof getNotificationData> = yield select(
      getNotificationData
    );

    if (isEmpty(notificationTypes)) {
      const notificationTypesFromServer = yield* call(
        notificationsApi.getNotificationTypes
      );

      yield put(
        notificationsActions.setNotificationTypes({
          notificationTypes: notificationTypesFromServer,
        })
      );
    }

    yield call(initNotifications);
  } catch (e) {
    handleServerError(e);
    console.error(e);
  } finally {
    yield put(notificationsActions.finishInit());
  }
}

function* handleRefresh() {
  try {
    yield put(notificationsActions.setSelectedIds({ ids: [] }));
    yield put(notificationsActions.setIsLoading({ isLoading: true }));

    yield call(initNotifications);

    yield put(notificationsActions.setIsLoading({ isLoading: false }));
  } catch (e) {
    console.error(e);
  }
}

function* initNotifications() {
  try {
    const { page, pageSize } = yield* select(
      gridSelector,
      NOTIFICATIONS_GRID_NAME
    );

    const { eventType } = yield* select(getNotificationData);

    const buFilterValue = yield* call(getBUFilterValue);

    const notificationsModel = yield* call(notificationsApi.getNotifications, {
      offset: (page - 1) * pageSize,
      limit: pageSize,
      notificationType:
        eventType === NOTIFICATION_TYPE_ALL ? undefined : eventType,
      businessUnitsIds: buFilterValue,
    });

    yield put(
      notificationsActions.setUnreadMessages({
        unreadMessages: notificationsModel.unreadCount,
      })
    );

    yield put(
      crmGridInit(NOTIFICATIONS_GRID_NAME, {
        items: mapNotificationsToGridItems(notificationsModel.notifications),
        totalCount: notificationsModel.totalCount,
      })
    );

    yield put(crmGridSetPage(NOTIFICATIONS_GRID_NAME, page));
  } catch (e) {
    console.error(e);
  }
}

function* handleMarkAsRead(action: NotificationsMarkAsReadAction) {
  try {
    yield put(notificationsActions.setIsLoading({ isLoading: true }));

    const { unreadMessages }: GRT<typeof getNotificationData> = yield select(
      getNotificationData
    );
    const { items } = yield* select((state) =>
      gridSelector<INotificationGridItem>(state, NOTIFICATIONS_GRID_NAME)
    );

    yield notificationsApi.markAsRead(action.payload.ids);

    const newItems = updateNotificationGridItemsIsRead(
      items as INotificationGridItem[],
      action.payload.ids
    );
    const newUnreadMessages = unreadMessages - action.payload.ids.length;

    yield put(crmGridSetItems(NOTIFICATIONS_GRID_NAME, newItems));
    yield put(
      notificationsActions.setUnreadMessages({
        unreadMessages: newUnreadMessages,
      })
    );
    yield put(notificationsActions.setIsLoading({ isLoading: false }));
  } catch (e) {
    console.error(e);
  }
}

function* handleDelete(action: NotificationsDeleteAction) {
  try {
    yield put(notificationsActions.setIsLoading({ isLoading: true }));

    const { items, page } = yield* select((state) =>
      gridSelector<INotificationGridItem>(state, NOTIFICATIONS_GRID_NAME)
    );

    yield notificationsApi.deleteNotifications(action.payload.ids);

    const newItems = deleteNotificationGridItems(
      items as INotificationGridItem[],
      action.payload.ids
    );
    const needChangePage = page !== 1 && isEmpty(newItems);

    if (needChangePage) {
      const newPage = page - 1;
      yield put(crmGridSetPage(NOTIFICATIONS_GRID_NAME, newPage));
    }

    yield put(notificationsActions.refresh());
  } catch (e) {
    console.error(e);
  }
}

function* handleNewReceived(action: NotificationsNewReceivedAction) {
  try {
    const { notification } = action.payload;

    const {
      isReady,
      eventType,
      unreadMessages,
      selectedIds,
    }: GRT<typeof getNotificationData> = yield select(getNotificationData);

    const { page, totalCount, items } = yield* select((state) =>
      gridSelector<INotificationGridItem>(state, NOTIFICATIONS_GRID_NAME)
    );

    const needUpdateStore =
      isReady &&
      (eventType === NOTIFICATION_TYPE_ALL ||
        eventType === notification.notificationType);

    if (needUpdateStore) {
      yield put(
        notificationsActions.setUnreadMessages({
          unreadMessages: unreadMessages + 1,
        })
      );

      yield put(
        crmGridSetTotalCount(NOTIFICATIONS_GRID_NAME, Number(totalCount) + 1)
      );

      if (page === 1) {
        const newItem = mapNotificationToGridItem(notification);
        const newItems = [...items];
        const removedItem = newItems.pop();
        newItems.unshift(newItem);

        const newSelectedIds = selectedIds.filter(
          (selectedId) => selectedId !== removedItem?.notificationDetails.id
        );

        yield put(notificationsActions.setSelectedIds({ ids: newSelectedIds }));
        yield put(crmGridSetItems(NOTIFICATIONS_GRID_NAME, newItems));
      }
    }

    const notificationMessage = getNotificationMessage(
      action.payload.notification
    );

    notify.info(notificationMessage);
  } catch (e) {
    console.error(e);
  }
}
